import React, { useState } from 'react';
import Card from '../muiComponents/Card';
import IconButton from '../muiComponents/IconButton';
import ResultsIndicator from '../property-partners-portal/formsProperty/Ari/ResultsIndicator';
import useTranslate from '../customHooks/useTranslate';
import { Typography } from '@mui/material';
import '../stylesheets/stylesComponents/filters-bar.scss';

interface FiltersBarProps {
    title: string;
    currentResults: number;
    totalResults: number;
    isLoading: boolean;
    children: React.ReactNode;
    clearFilters: () => void;
    search: () => void;
    translateTitle?: boolean;
    startButtonBar?: React.ReactNode;
    endButtonBar?: React.ReactNode;
    isCollapsed?: boolean;
    toggleCollapse?: () => void;
}

const FiltersBar = (props: FiltersBarProps) => {
    const {
        title,
        translateTitle = true,
        currentResults,
        totalResults,
        isLoading,
        children,
        clearFilters,
        search,
        startButtonBar,
        endButtonBar,
        isCollapsed,
        toggleCollapse,
    } = props;

    const [filtersOpen, setFiltersOpen] = useState(true);

    const trnTitle = useTranslate(title);
    const searchTitle = useTranslate('search');
    const clearFiltersTitle = useTranslate('clear_filters');
    const toggleIconName = filtersOpen ? 'FilterAltOff' : 'FilterAlt';
    const filtersClassName = `filter-bar__filters ${
        filtersOpen ? '--expanded' : '--collapsed'
    }`;

    const searchButtonsClassName = `filter-bar__search-buttons ${
        filtersOpen ? '--expanded' : '--collapsed'
    }`;

    const toggleFilters = () => {
        setFiltersOpen(prevState => !prevState);
    };

    const isCollapsable =
        isCollapsed !== undefined && toggleCollapse !== undefined;

    return (
        <div className='filter-bar'>
            <div className='filter-bar__header'>
                <div className='filter-bar__info'>
                    <Typography
                        variant='h1'
                        onClick={() => {
                            if (isCollapsable) {
                                toggleCollapse();
                                setFiltersOpen(isCollapsed);
                            }
                        }}
                        className={isCollapsable ? 'cursor-pointer' : ''}
                    >
                        {translateTitle ? trnTitle : title}
                    </Typography>
                    {!isCollapsable || (isCollapsable && !isCollapsed) ? (
                        <ResultsIndicator
                            currentResults={currentResults}
                            totalResults={totalResults}
                            isLoading={isLoading}
                        />
                    ) : (
                        ''
                    )}
                </div>
                <div className='filter-bar__buttons'>
                    {filtersOpen && startButtonBar}
                    <div className='flex flex-align-center gap-20'>
                        <div className={searchButtonsClassName}>
                            <IconButton
                                shape='rounded'
                                iconName='SearchOff'
                                onClick={() => clearFilters()}
                                title={
                                    filtersOpen ? clearFiltersTitle : undefined
                                }
                                iconProps={{ fontSize: 'medium' }}
                                disabled={isLoading || !filtersOpen}
                            />
                            <IconButton
                                shape='rounded'
                                iconName='Search'
                                onClick={() => search()}
                                title={filtersOpen ? searchTitle : undefined}
                                iconProps={{ fontSize: 'medium' }}
                                disabled={isLoading || !filtersOpen}
                            />
                        </div>
                        {!isCollapsable || (isCollapsable && !isCollapsed) ? (
                            <IconButton
                                shape='rounded'
                                iconProps={{ fontSize: 'medium' }}
                                iconName={toggleIconName}
                                onClick={toggleFilters}
                            />
                        ) : (
                            ''
                        )}
                    </div>

                    {endButtonBar}
                    {isCollapsable && (
                        <IconButton
                            iconName={
                                isCollapsed
                                    ? 'KeyboardArrowDown'
                                    : 'KeyboardArrowUp'
                            }
                            fontSize='medium'
                            onClick={() => {
                                toggleCollapse();
                                setFiltersOpen(isCollapsed);
                            }}
                        />
                    )}
                </div>
            </div>
            <Card>
                <div className={filtersClassName}>{children}</div>
            </Card>
        </div>
    );
};

export default FiltersBar;
